/**
 * GeneralStatusBadge — Iter53 P50 Faza B UI
 *
 * Badge distinct pentru cele 8 statusuri general. Tooltip cu:
 *   - motivul calculării
 *   - rule code
 *   - data calculării
 *   - versiunea calculatorului
 */
import { AlertTriangle, AlertOctagon, CheckCircle, Clock, HelpCircle, Send, Ban, RotateCcw, RefreshCw } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";

const STATUS_STYLES = {
  "Nou":                 { cls: "bg-slate-100 text-slate-700 border-slate-300",   Icon: HelpCircle },
  "În verificare BO":    { cls: "bg-purple-50 text-purple-800 border-purple-300", Icon: Clock },
  "În procesare PPC":    { cls: "bg-blue-50 text-blue-800 border-blue-300",       Icon: Send },
  "În curs de activare": { cls: "bg-cyan-50 text-cyan-800 border-cyan-300",       Icon: Clock },
  "Activ":               { cls: "bg-emerald-50 text-emerald-800 border-emerald-300", Icon: CheckCircle },
  "Problemă":            { cls: "bg-rose-50 text-rose-800 border-rose-300",       Icon: AlertOctagon },
  "Anulat":              { cls: "bg-orange-50 text-orange-800 border-orange-300", Icon: Ban },
  "Stornat":             { cls: "bg-red-100 text-red-900 border-red-400",         Icon: RotateCcw },
};

export const GeneralStatusBadge = ({ sale, showConflicts = true, compact = false }) => {
  const status = sale?.general_status;
  if (!status) return <span className="text-xs text-slate-400" data-testid="general-status-empty">—</span>;
  const style = STATUS_STYLES[status] || STATUS_STYLES["Nou"];
  const { Icon, cls } = style;

  const tooltipContent = (
    <div className="space-y-1 text-xs max-w-xs">
      <div className="font-semibold">{status}</div>
      {sale.general_status_reason && <div className="opacity-90">{sale.general_status_reason}</div>}
      {sale.general_status_rule_code && <div>Regulă: <code>{sale.general_status_rule_code}</code></div>}
      {sale.general_status_source && <div>Sursă: {sale.general_status_source}</div>}
      {sale.general_status_calculated_at && (
        <div>Calculat: {new Date(sale.general_status_calculated_at).toLocaleString("ro-RO")}</div>
      )}
      {sale.general_status_calc_version && <div>Versiune: {sale.general_status_calc_version}</div>}
    </div>
  );

  const conflictsCount = sale.general_status_conflicts_count ?? 0;
  const warningsCount = sale.general_status_warnings_count ?? 0;

  return (
    <TooltipProvider delayDuration={300}>
      <div className="inline-flex items-center gap-1.5">
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-medium ${cls}`}
              data-testid={`general-status-badge-${sale.id}`}
            >
              <Icon className="w-3 h-3" />
              {!compact && <span>{status}</span>}
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">{tooltipContent}</TooltipContent>
        </Tooltip>

        {showConflicts && sale.has_blocking_conflict && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span data-testid={`gs-conflict-badge-${sale.id}`}
                    className="inline-flex items-center rounded bg-red-100 border border-red-300 px-1.5 py-0.5 text-[10px] text-red-800 font-semibold">
                <AlertOctagon className="w-3 h-3 mr-0.5" />{conflictsCount}
              </span>
            </TooltipTrigger>
            <TooltipContent>Conflict blocant ({conflictsCount})</TooltipContent>
          </Tooltip>
        )}
        {showConflicts && warningsCount > 0 && !sale.has_blocking_conflict && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span data-testid={`gs-warning-badge-${sale.id}`}
                    className="inline-flex items-center rounded bg-amber-50 border border-amber-300 px-1.5 py-0.5 text-[10px] text-amber-800">
                <AlertTriangle className="w-3 h-3 mr-0.5" />{warningsCount}
              </span>
            </TooltipTrigger>
            <TooltipContent>{warningsCount} avertisment{warningsCount === 1 ? "" : "e"}</TooltipContent>
          </Tooltip>
        )}
        {/* Faza B v2 — Stale indicator (source updated after calculated_at) */}
        {sale.is_general_status_stale && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span data-testid={`gs-stale-badge-${sale.id}`}
                    className="inline-flex items-center rounded bg-yellow-50 border border-yellow-300 px-1.5 py-0.5 text-[10px] text-yellow-800">
                <RefreshCw className="w-3 h-3 mr-0.5" />
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <div className="space-y-1 text-xs">
                <div className="font-semibold">Status posibil neactualizat</div>
                <div>Surse mai noi decât ultima calculare:</div>
                <ul className="list-disc list-inside opacity-90">
                  {(sale.general_status_stale_sources || []).map((s) => (
                    <li key={s}><code>{s}</code></li>
                  ))}
                </ul>
              </div>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
};

export const GENERAL_STATUSES_ALL = [
  "Nou", "În verificare BO", "În procesare PPC", "În curs de activare",
  "Activ", "Problemă", "Anulat", "Stornat",
];

export default GeneralStatusBadge;
